import { useStyles, Dialog, pxToRem, Grid } from '@archipro-design/aria';

import * as S from './CompanyImages.style';
import type { CompanyImage } from '@modules/professional/type/company-images';
import ImageSlide from './ImageSlide';
import { CloseLine } from '@archipro-design/icons';
import { useEffect, useRef, useState } from 'react';

interface PhotoViewerModalProps {
    professional: {
        id: number;
        title: string;
        companyLogoSrc: string;
        logoBackgroundColor: string;
        blockSummary: string;
    };
    stack: Array<CompanyImage>;
    imageId: number;
    open: boolean;
    toggle: () => void;
}

interface SlideProps {
    key: string;
    id: number;
    content: JSX.Element;
}

const PhotoViewerModal = ({
    stack,
    open,
    toggle,
    imageId,
}: PhotoViewerModalProps) => {
    const divRefs = useRef(new Map<number, HTMLDivElement>());
    const [scrolled, setScrolled] = useState(false);
    const { css } = useStyles({ scrolled });

    const items: SlideProps[] = stack.flatMap((slide) => {
        return {
            key: `${slide.ID}-ImageSlide`,
            id: slide.ID,
            content: (
                <div
                    key={`${slide.ID}-ImageSlide`}
                    className={css(S.StackSlideContainer)}
                    ref={(ref) => ref && divRefs.current.set(slide.ID, ref)}
                >
                    <ImageSlide slide={slide} />
                </div>
            ),
        };
    });

    useEffect(() => {
        if (!open) return;

        setTimeout(() => {
            divRefs.current.get(imageId)?.scrollIntoView();
            setScrolled(true);
        }, 200);
    }, [imageId, divRefs, open]);

    return (
        <Dialog
            open={open}
            onCancel={() => toggle()}
            className={css(S.PhotoViewerDialog)}
            headerAction={{
                icon: (
                    <CloseLine
                        className={css({
                            width: `${pxToRem(40)} !important`,
                            height: `${pxToRem(40)} !important`,
                        })}
                    />
                ),
                title: 'Close',
                onClick: () => toggle(),
            }}
            content={{
                content: (
                    <Grid columns={1} className={css(S.StackContainer)}>
                        {items.map((s) => s.content)}
                    </Grid>
                ),
            }}
            variables={{
                rootWidth: pxToRem(1920),
                rootMaxHeight: '100vh',
                overflow: true,
            }}
        />
    );
};

export default PhotoViewerModal;
